import { LoanType } from '../../../loans/common/LoanConstants';
import { applicationActions } from '@apply/adminStore';
import { AppraiseStageType } from '../../appraise/common/Constants';
import { ApplicationActionType, PropertyStatusType } from '../ApplicationConstants';
import { ApplicationStatusType } from '@apply/constants';
import { PermissionType } from '@apply/constants';
import { hasPermission } from '@apply/ui';

export interface IAppRowActionOptions {
    id: number;
    propertyId?: number | null;
    loanTypeId: LoanType;
    appStatusId: ApplicationStatusType;
    propertyStatusId: PropertyStatusType;
    isPortfolio: boolean;
    disabled: boolean;
    canConvertToLoan: boolean;
}

export interface IAppAction {
    id: ApplicationActionType;
    text: string;
    applicationId: number;
    propertyId?: number | null;
    icon?: string | null;
    stylingMode?: string | null;
    disabled: boolean;
    type?: string | null;
}

export const getApplicationActions = (appRow: IAppRowActionOptions, isPropertyLevel = false) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const actions: IAppAction[] = [];

    if (!isPropertyLevel && appRow.appStatusId === ApplicationStatusType.SubmittedForReview) {
        actions.push({
            id: ApplicationActionType.Review,
            text: 'Review',
            applicationId: appRow.id,
            propertyId: appRow.propertyId,
            icon: 'edit',
            stylingMode: 'contained',
            disabled: false,
        });

        const convertTypes = [LoanType.FixFlip, LoanType.SubNoteFixFlip, LoanType.Construction, LoanType.SubNoteConstruction];
        if (
            convertTypes.includes(appRow.loanTypeId) &&
            (appRow.appStatusId === ApplicationStatusType.SubmittedForReview ||
                appRow.appStatusId === ApplicationStatusType.Appraise)
        ) {
            actions.push({
                id: ApplicationActionType.ChangeLoanType,
                text: 'Change Loan Type',
                applicationId: appRow.id,
                stylingMode: 'contained',
                disabled: false,
            });
        }
    }

    if (appRow.appStatusId === ApplicationStatusType.Appraise) {
        if (!appRow.disabled && appRow.propertyStatusId === PropertyStatusType.Appraise) {
            actions.push({
                id: ApplicationActionType.AppraisalOrder,
                text: 'Order Appraisal',
                applicationId: appRow.id,
                propertyId: appRow.propertyId,
                disabled: false,
            });
        }

        if (!appRow.disabled && appRow.propertyStatusId === PropertyStatusType.AppraisalReviewed) {
            actions.push({
                id: ApplicationActionType.AppraisalView,
                text: 'View Appraisal',
                applicationId: appRow.id,
                propertyId: appRow.propertyId,
                disabled: false,
            });
        }

        if (!appRow.disabled && appRow.propertyStatusId === PropertyStatusType.AppraisalOrdered) {
            actions.push({
                id: ApplicationActionType.AppraisalReceive,
                text: 'Receive Appraisal',
                applicationId: appRow.id,
                propertyId: appRow.propertyId,
                disabled: false,
            });
        }

        if (!appRow.disabled && appRow.propertyStatusId === PropertyStatusType.AppraisalReceived) {
            actions.push({
                id: ApplicationActionType.AppraisalReview,
                text: 'Review Appraisal',
                applicationId: appRow.id,
                propertyId: appRow.propertyId,
                disabled: false,
            });
        }

        if (
            !appRow.disabled &&
            (appRow.propertyStatusId === PropertyStatusType.AppraisalReviewed ||
                appRow.propertyStatusId === PropertyStatusType.AppraisalOrdered ||
                appRow.propertyStatusId === PropertyStatusType.AppraisalReceived)
        ) {
            actions.push({
                id: ApplicationActionType.AppraisalNotes,
                text: 'Notes/Details',
                applicationId: appRow.id,
                propertyId: appRow.propertyId,
                disabled: false,
            });
        }

        if (!isPropertyLevel && appRow?.canConvertToLoan) {
            actions.push({
                id: ApplicationActionType.ConvertToLoan,
                text: 'Convert to Loan',
                applicationId: appRow.id,
                disabled: false,
            });
        }
    }

    if (
        !appRow.disabled &&
        (appRow.propertyStatusId === PropertyStatusType.Appraise ||
            appRow.propertyStatusId === PropertyStatusType.AppraisalOrdered ||
            appRow.propertyStatusId === PropertyStatusType.AppraisalReceived ||
            appRow.propertyStatusId === PropertyStatusType.AppraisalReviewed)
    ) {
        actions.push({
            id: ApplicationActionType.Rollback,
            text: 'Rollback',
            applicationId: appRow.id,
            propertyId: appRow.propertyId,
            disabled: false,
        });

        actions.push({
            id: ApplicationActionType.ApplicationFiles,
            text: 'View Files',
            applicationId: appRow.id,
            propertyId: appRow.propertyId,
            disabled: false,
        });
    }

    if (
        !isPropertyLevel &&
        !appRow.disabled &&
        (appRow.appStatusId === ApplicationStatusType.RecycleBin_Duplicate ||
            appRow.appStatusId === ApplicationStatusType.IncompleteFile ||
            appRow.appStatusId === ApplicationStatusType.RecycleBin_MadeInError ||
            appRow.appStatusId === ApplicationStatusType.RecycleBin_Testing)
    ) {
        actions.push({
            id: ApplicationActionType.Restore,
            text: 'Restore',
            applicationId: appRow.id,
            disabled: false,
        });

        actions.push({
            id: ApplicationActionType.ApplicationFiles,
            text: 'View Files',
            applicationId: appRow.id,
            disabled: false,
        });
    }

    if (
        appRow.appStatusId === ApplicationStatusType.Appraise ||
        appRow.appStatusId === ApplicationStatusType.SubmittedForReview
    ) {
        actions.push({
            id: ApplicationActionType.DeleteOrMoveProperties,
            text: isPropertyLevel ? 'Del. / Move Prop.' : 'Del. / Move Props',
            applicationId: appRow.id,
            propertyId: appRow.propertyId,
            disabled: false,
        });
    }

    if (
        !isPropertyLevel &&
        !appRow.disabled &&
        (appRow.appStatusId === ApplicationStatusType.Appraise ||
            appRow.appStatusId === ApplicationStatusType.SubmittedForReview ||
            appRow.appStatusId === ApplicationStatusType.SubmittedByApplicant)
    ) {
        actions.push({
            id: ApplicationActionType.LostOrRecycle,
            text: 'Lost/Delete',
            applicationId: appRow.id,
            propertyId: appRow.propertyId,
            disabled: false,
            type: 'danger',
            icon: 'remove',
        });
    }

    if (
        appRow.appStatusId === ApplicationStatusType.Lost_DeclinedByBfi ||
        appRow.appStatusId === ApplicationStatusType.Lost_WithdrawnByClient ||
        appRow.appStatusId === ApplicationStatusType.RecycleBin_Duplicate ||
        appRow.appStatusId === ApplicationStatusType.RecycleBin_MadeInError ||
        appRow.appStatusId === ApplicationStatusType.RecycleBin_Testing ||
        appRow.appStatusId === ApplicationStatusType.IncompleteFile ||
        appRow.appStatusId === ApplicationStatusType.RecycleBin_Reassigned
    ) {
        actions.push({
            id: ApplicationActionType.Summary,
            text: 'Summary',
            applicationId: appRow.id,
            stylingMode: 'contained',
            disabled: false,
        });
    }

    if (!isPropertyLevel) {
        if (
            appRow.appStatusId === ApplicationStatusType.SubmittedForReview ||
            appRow.appStatusId === ApplicationStatusType.Appraise
        ) {
            actions.push({
                id: ApplicationActionType.SwapPrincipal,
                text: 'Swap Principal',
                applicationId: appRow.id,
                stylingMode: 'contained',
                disabled: !hasPermission(PermissionType.ApplicationPrincipalSwap),
            });
        }

        actions.push({
            id: ApplicationActionType.AuditHistory,
            text: 'Audit History',
            applicationId: appRow.id,
            propertyId: appRow.propertyId,
            disabled: false,
        });
    }

    return actions;
};

export const onApplicationActionItem = (dispatch, actionId: ApplicationActionType, appId: number, propertyId?: number | null) => {
    switch (actionId) {
        case ApplicationActionType.Review:
            openApplicationReview(dispatch, appId, propertyId);
            break;
        case ApplicationActionType.AppraisalOrder:
            openAppraisalOrder(dispatch, appId, propertyId);
            break;
        case ApplicationActionType.AppraisalReceive:
            openAppraisalReceive(dispatch, appId, propertyId);
            break;
        case ApplicationActionType.AppraisalReview:
            openAppraisalReview(dispatch, appId, propertyId);
            break;
        case ApplicationActionType.AppraisalView:
            openAppraisalView(dispatch, appId, propertyId);
            break;
        case ApplicationActionType.LostOrRecycle:
            openDeclineOrRecycleModal(dispatch, appId);
            break;
        case ApplicationActionType.Rollback:
            onRollback(dispatch, appId, propertyId);
            break;
        case ApplicationActionType.ConvertToLoan:
            onConvertToLoan(dispatch, appId, propertyId);
            break;
        case ApplicationActionType.AppraisalNotes:
            openAppraisalNotes(dispatch, appId, propertyId);
            break;
        case ApplicationActionType.ChangeLoanType:
            openChangeLoanType(dispatch, appId);
            break;
        case ApplicationActionType.Restore:
            openRestoreLoan(dispatch, appId);
            break;
        case ApplicationActionType.DeleteOrMoveProperties:
            openDeleteOrMoveProperties(dispatch, appId, propertyId);
            break;
        case ApplicationActionType.Summary:
            openApplicationSummary(dispatch, appId);
            break;
        case ApplicationActionType.AuditHistory:
            openApplicationAuditHistory(dispatch, appId, propertyId);
            break;
        case ApplicationActionType.SwapPrincipal:
            openSwapPrincipalModal(dispatch, appId, propertyId);
            break;
        case ApplicationActionType.ApplicationFiles:
            openApplicationFileModal(dispatch, appId, propertyId);
            break;
    }
};

const openApplicationReview = (dispatch, id: number, propertyId?: number | null) => {
    dispatch(
        applicationActions.setApplicationReviewModal({
            applicationReviewModal: { isOpen: true, id, propertyId },
        })
    );
};

const openApplicationSummary = (dispatch, id: number) => {
    dispatch(
        applicationActions.setApplicationSummaryModal({
            applicationSummaryModal: { isOpen: true, id },
        })
    );
};

const openAppraisalOrder = (dispatch, id: number, propertyId?: number | null) => {
    dispatch(
        applicationActions.setAppraisalModal({
            appraisalModal: {
                isOpen: true,
                id,
                stage: AppraiseStageType.Order,
                propertyId,
            },
        })
    );
};

const openAppraisalReceive = (dispatch, id: number, propertyId?: number | null) => {
    dispatch(
        applicationActions.setAppraisalModal({
            appraisalModal: {
                isOpen: true,
                id,
                stage: AppraiseStageType.Receive,
                propertyId,
            },
        })
    );
};

const openAppraisalReview = (dispatch, id: number, propertyId?: number | null) => {
    dispatch(
        applicationActions.setAppraisalModal({
            appraisalModal: {
                isOpen: true,
                id,
                stage: AppraiseStageType.Review,
                propertyId,
            },
        })
    );
};

const openAppraisalView = (dispatch, id: number, propertyId?: number | null) => {
    dispatch(
        applicationActions.setAppraisalModal({
            appraisalModal: {
                isOpen: true,
                id,
                stage: AppraiseStageType.Review,
                propertyId,
                readOnly: true,
            },
        })
    );
};

const openAppraisalNotes = (dispatch, id: number, propertyId?: number | null) => {
    dispatch(
        applicationActions.setAppraisalModal({
            appraisalModal: {
                isOpen: true,
                id,
                stage: AppraiseStageType.Review,
                propertyId,
                notesOnly: true,
            },
        })
    );
};

const openDeclineOrRecycleModal = (dispatch, id: number) => {
    dispatch(
        applicationActions.setDeclineOrRecycleModal({
            declineOrRecycleModal: { isOpen: true, id },
        })
    );
};

const onRollback = (dispatch, id: number, propertyId?: number | null) => {
    dispatch(
        applicationActions.setRollbackModal({
            rollbackModal: { isOpen: true, id, propertyId },
        })
    );
};

const onConvertToLoan = (dispatch, id: number, propertyId?: number | null) => {
    dispatch(
        applicationActions.setConvertToLoanModal({
            convertToLoanModal: { isOpen: true, id, propertyId },
        })
    );
};

const openChangeLoanType = (dispatch, id: number) => {
    dispatch(
        applicationActions.setChangeAppLoanTypeModal({
            changeAppLoanTypeModal: { isOpen: true, appId: id },
        })
    );
};

const openRestoreLoan = (dispatch, id: number) => {
    dispatch(
        applicationActions.setRestoreApplicationModal({
            restoreApplicationModal: { isOpen: true, id },
        })
    );
};

const openDeleteOrMoveProperties = (dispatch, id: number, propertyId?: number | null) => {
    dispatch(
        applicationActions.setMovePropertiesModal({
            movePropertiesModal: { isOpen: true, id, propertyId },
        })
    );
};

const openApplicationAuditHistory = (dispatch, id: number, propertyId?: number | null) => {
    dispatch(
        applicationActions.setAuditHistoryModal({
            auditHistoryModal: { isOpen: true, id, propertyId },
        })
    );
};

const openSwapPrincipalModal = (dispatch, id: number) => {
    dispatch(
        applicationActions.setPrincipalSwapModal({
            principalSwapModal: { isOpen: true, id },
        })
    );
};

const openApplicationFileModal = (dispatch, id: number, propertyId?: number | null) => {
    dispatch(
        applicationActions.setApplicationFilesModal({
            applicationFilesModal: { isOpen: true, id, propertyId },
        })
    );
};
